import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { PhotoViewerModal } from '@/components/ui/photo-viewer-modal';
import { SignedImage } from '@/components/ui/signed-image';
import { TaskReactionsComments } from './TaskReactionsComments';
import { VitalSparkline } from './VitalSparkline';
import { EditVitalExecutionDialog, type EditVitalExecutionMode } from './EditVitalExecutionDialog';
import { usePatientRole } from '@/hooks/usePatientRole';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { extractPathFromUrl } from '@/lib/storage-utils';
import {
  Pill,
  Activity,
  Droplets,
  Utensils,
  AlertTriangle,
  CheckCircle2,
  Heart,
  Thermometer,
  Weight,
  HeartPulse,
  ImageOff,
  Dumbbell,
  MoreVertical,
  Trash2,
  Pencil
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FOOD_ACCEPTANCE_LABELS, type FoodAcceptance } from '@/types/caregiver';

interface TaskFeedItemProps {
  execution: {
    id: string;
    executed_at: string;
    is_alert: boolean;
    is_late?: boolean;
    notes: string | null;
    photo_url: string | null;
    vital_value: number | null;
    vital_value_secondary: number | null;
    food_acceptance: string | null;
    is_sporadic: boolean;
    task_id?: string | null;
    adhoc_task_type?: string | null;
    patient_id?: string;
    task: {
      name: string;
      task_type: string;
      vital_type?: string | null;
      unit?: string | null;
      incident_type?: string | null;
      min_alert_value?: number | null;
      max_alert_value?: number | null;
    };
    executor?: {
      full_name: string | null;
      avatar_url?: string | null;
    } | null;
    // Preloaded counts to avoid N+1 queries
    reactions_count?: number;
    comments_count?: number;
    reactions?: Array<{ id: string; emoji: string; user_id: string }>;
  };
  patientId?: string;
  isOwner?: boolean;
  isHighlighted?: boolean;
  autoOpenComments?: boolean;
  highlightCommentId?: string | null;
  isViewerTask?: boolean;
  /** Recent vital readings for sparkline (last 7) */
  recentVitalReadings?: Array<{ value: number; isAlert?: boolean }>;
}

const taskTypeIcons: Record<string, React.ReactNode> = {
  fixed_medication: <Pill className="w-4 h-4" />,
  sporadic_medication: <Pill className="w-4 h-4" />,
  vital_sign: <Activity className="w-4 h-4" />,
  food: <Utensils className="w-4 h-4" />,
  hygiene: <Droplets className="w-4 h-4" />,
  incident: <AlertTriangle className="w-4 h-4" />,
  physical_activity: <Dumbbell className="w-4 h-4" />,
  other: <CheckCircle2 className="w-4 h-4" />
};

const vitalTypeIcons: Record<string, React.ReactNode> = {
  blood_pressure: <Heart className="w-4 h-4" />,
  glucose: <Droplets className="w-4 h-4" />,
  temperature: <Thermometer className="w-4 h-4" />,
  oxygen_saturation: <Activity className="w-4 h-4" />,
  weight: <Weight className="w-4 h-4" />,
  heart_rate: <HeartPulse className="w-4 h-4" />
};

export function TaskFeedItem({ execution, patientId, isOwner = false, isHighlighted = false, autoOpenComments = false, highlightCommentId, isViewerTask, recentVitalReadings }: TaskFeedItemProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(execution.photo_url);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editVitalState, setEditVitalState] = useState<EditVitalExecutionMode | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const hasScrolled = useRef(false);
  const { canEdit, isPatientActive } = usePatientRole(execution.patient_id || patientId);
  const queryClient = useQueryClient();
  const canEditVital = !!(execution.patient_id || patientId) && canEdit && isPatientActive;

  const handleDeletePhoto = async () => {
    if (!photoUrl) return;

    setIsDeleting(true);
    try {
      // Delete from bucket
      const filePath = extractPathFromUrl(photoUrl, 'shift-photos');
      if (filePath) {
        await supabase.storage.from('shift-photos').remove([filePath]);
      }

      // Update database
      await supabase
        .from('task_executions')
        .update({ photo_url: null })
        .eq('id', execution.id);

      setPhotoUrl(null);
      toast.success('Foto excluída permanentemente');
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Erro ao excluir foto');
    } finally {
      setIsDeleting(false);
    }
  };

  // Scroll into view and animate when highlighted - only once
  useEffect(() => {
    if (isHighlighted && cardRef.current && !hasScrolled.current) {
      hasScrolled.current = true;
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Start animation and stop after 2 pulses (about 2 seconds)
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isHighlighted]);
  const { task } = execution;
  const isIncident = task.task_type === 'incident' || execution.adhoc_task_type === 'incident';
  const isVital = task.task_type === 'vital_sign';
  const isFood = task.task_type === 'food';
  const isSporadicMed = task.task_type === 'sporadic_medication' || execution.adhoc_task_type === 'sporadic_medication';

  const getIcon = () => {
    if (isVital && task.vital_type) {
      return vitalTypeIcons[task.vital_type] || taskTypeIcons.vital_sign;
    }
    return taskTypeIcons[task.task_type] || taskTypeIcons.other;
  };

  // REACTIONS & COMMENTS
  const [totalComments, setTotalComments] = useState(0);

  const formatVitalValue = () => {
    if (!execution.vital_value) return null;

    if (task.vital_type === 'blood_pressure' && execution.vital_value_secondary) {
      return `${execution.vital_value} x ${execution.vital_value_secondary}`;
    }

    return `${execution.vital_value} ${task.unit || ''}`;
  };

  const getFoodAcceptanceLabel = () => {
    if (!execution.food_acceptance) return null;
    return t(`food.acceptance.${execution.food_acceptance}`, { defaultValue: execution.food_acceptance });
  };

  // Determine which badge to show - only one badge per item
  const getBadge = () => {
    // Priority 1: Critical (Incident)
    if (isIncident) {
      return (
        <Badge variant="destructive" className="text-[12px]">
          {t('familyDashboard.feed.badge.incident')}
        </Badge>
      );
    }
    // Priority 2: Alert (Medical warnings)
    if (execution.is_alert) {
      return (
        <Badge variant="destructive" className="text-[12px]">
          {t('familyDashboard.feed.badge.alert')}
        </Badge>
      );
    }
    // Priority 3: Late (Schedule issues)
    if (execution.is_late) {
      return (
        <Badge variant="outline" className="text-warning border-warning text-[12px]">
          {t('familyDashboard.feed.badge.late')}
        </Badge>
      );
    }
    // Priority 4: Nature of task (Sporadic Med)
    if (isSporadicMed) {
      return (
        <Badge variant="outline" className="text-sporadic border-sporadic text-[12px]">
          {t('familyDashboard.feed.badge.medSporadic')}
        </Badge>
      );
    }
    // Priority 5: Nature of task (General Ad-hoc)
    if (!execution.task_id) {
      return (
        <Badge variant="secondary" className="text-[12px]">
          {t('familyDashboard.feed.badge.adhoc')}
        </Badge>
      );
    }
    return null;
  };

  return (
    <Card
      ref={cardRef}
      className={cn(
        "rounded-xl overflow-hidden transition-all duration-500",
        isHighlighted ? "bg-primary/5 border-primary/20 ring-1 ring-primary/20" : "bg-card hover:bg-muted/30 border-border/50",
        isViewerTask && !isHighlighted && "border-l-4 border-l-primary/60", // Highlight viewer's tasks
        isAnimating && "animate-pulse"
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-3 pb-2">
        <SignedAvatar
          src={execution.executor?.avatar_url}
          bucket="avatars"
          className="w-8 h-8"
          fallback={
            <span className="bg-muted text-xs w-full h-full flex items-center justify-center rounded-full">
              {execution.executor?.full_name?.charAt(0) || '?'}
            </span>
          }
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {execution.executor?.full_name || t('familyDashboard.feed.unknownCaregiver')}
          </p>
          <p className="text-xs text-muted-foreground">
            {format(new Date(execution.executed_at), "dd 'de' MMM 'às' HH:mm", { locale: ptBR })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {getBadge()}
          {(isOwner && photoUrl) || (isVital && canEditVital) ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isVital && canEditVital && (
                  <DropdownMenuItem
                    onClick={() => setEditVitalState({
                      mode: 'edit',
                      executionId: execution.id,
                      vitalType: task.vital_type ?? null,
                      initialValue: execution.vital_value,
                      initialSecondary: execution.vital_value_secondary,
                      initialNotes: execution.notes,
                      taskName: task.name,
                    })}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Editar medição
                  </DropdownMenuItem>
                )}
                {isOwner && photoUrl && (
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('feed.deletePhoto')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      {/* Delete Photo Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('feed.deletePhotoConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">{t('feed.deletePhotoConfirmDesc')}</span>
              <span className="block text-destructive font-medium">{t('feed.deletePhotoWarning')}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await handleDeletePhoto();
                setShowDeleteConfirm(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? t('common.processing') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Photo (Instagram-style) - Clickable */}
      {photoUrl && (
        <>
          <div
            className="w-full aspect-square bg-muted cursor-pointer"
            onClick={() => setPhotoViewerOpen(true)}
          >
            <SignedImage
              src={photoUrl}
              bucket="shift-photos"
              alt="Registro"
              className="w-full h-full object-cover hover:opacity-90 transition-opacity"
              fallback={
                <div className="w-full h-full flex items-center justify-center">
                  <ImageOff className="w-12 h-12 text-muted-foreground" />
                </div>
              }
            />
          </div>
          <PhotoViewerModal
            open={photoViewerOpen}
            onClose={() => setPhotoViewerOpen(false)}
            imageUrl={photoUrl}
            bucket="shift-photos"
            alt={t('familyDashboard.feed.photoAlt')}
            canDelete={isOwner}
            onDelete={handleDeletePhoto}
            isDeleting={isDeleting}
          />
        </>
      )}

      {/* Content */}
      <div className="p-3 pt-2">
        {/* Task Info */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`p-1.5 rounded-full ${isIncident
            ? 'bg-destructive/10 text-destructive'
            : execution.is_alert
              ? 'bg-destructive/10 text-destructive'
              : 'bg-primary/10 text-primary'
            }`}>
            {getIcon()}
          </div>
          <span className="font-medium text-sm">{task.name}</span>
        </div>

        {/* Vital Value Display */}
        {isVital && execution.vital_value && (
          <div className="space-y-1 mb-2">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${execution.is_alert
              ? 'bg-destructive/10 text-destructive'
              : 'bg-success/10 text-success'
              }`}>
              <span className="text-lg font-bold">{formatVitalValue()}</span>
              {execution.is_alert && <AlertTriangle className="w-4 h-4" />}
            </div>
            {execution.is_alert && (task.min_alert_value != null || task.max_alert_value != null) && (
              <p className="text-xs text-muted-foreground px-1">
                {t('executeTask.normalRange')}: {task.min_alert_value ?? '?'} – {task.max_alert_value ?? '?'} {task.unit || ''}
              </p>
            )}
            {recentVitalReadings && recentVitalReadings.length >= 2 && (
              <div className="px-1">
                <VitalSparkline
                  readings={recentVitalReadings}
                  min={task.min_alert_value}
                  max={task.max_alert_value}
                  unit={task.unit || ''}
                  currentValue={execution.vital_value}
                />
              </div>
            )}
          </div>
        )}

        {/* Food Acceptance Display */}
        {isFood && execution.food_acceptance && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-2 bg-warning/10 text-warning text-[12px]">
            <span className="font-medium">{t('familyDashboard.feed.badge.acceptance', { value: getFoodAcceptanceLabel() })}</span>
          </div>
        )}

        {/* Incident Type Display */}
        {isIncident && task.incident_type && (
          <Badge variant="destructive" className="mb-2 text-[12px]">
            {t(`incidents.${task.incident_type}`, { defaultValue: task.incident_type })}
          </Badge>
        )}

        {/* Notes */}
        {execution.notes && (
          <p className="text-sm text-foreground mt-2">
            {execution.notes}
          </p>
        )}

        {/* Reactions & Comments */}
        <div className="mt-3 pt-2 border-t">
          <TaskReactionsComments
            executionId={execution.id}
            autoOpen={autoOpenComments && isHighlighted}
            highlightCommentId={highlightCommentId}
            onCommentsCountChange={setTotalComments}
            initialReactionsCount={execution.reactions_count}
            initialCommentsCount={execution.comments_count}
            initialReactions={execution.reactions}
          />
        </div>
      </div>

      {(execution.patient_id || patientId) && (
        <EditVitalExecutionDialog
          open={!!editVitalState}
          onOpenChange={(o) => !o && setEditVitalState(null)}
          patientId={(execution.patient_id || patientId) as string}
          state={editVitalState}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['family-dashboard-feed'] });
            queryClient.invalidateQueries({ queryKey: ['daily-report'] });
            queryClient.invalidateQueries({ queryKey: ['shift-detail'] });
            queryClient.invalidateQueries({ queryKey: ['caregiver-agenda'] });
          }}
        />
      )}
    </Card>
  );
}
